import DealsApi from "@apis/deals";
import { Category, Product } from "@apis/index";
import Header from "@commons/header";
import Button from "@components/button";
import Wrapper from "@components/wrapper";
import useConfig from "@hooks/useConfig";
import React, { useEffect, useState } from "react";
import { FiFilter, FiGrid, FiList, FiPackage, FiSearch } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Form, Formik } from "formik";
import ProductCard from "@pages/admin/deals/products/ProductCard";
import { useSelector } from "react-redux";
import {
  CategorySelect,
  ControlBar,
  ControlContent,
  CountBadge,
  CountLabel,
  CountValue,
  EmptyIcon,
  EmptyState,
  FooterActions,
  LeftControls,
  LoadingContainer,
  ProductsGrid,
  RightControls,
  SearchIconWrapper,
  SearchInput,
  SearchWrapper,
  Spinner,
  StatCard,
  StatsBar,
  StatTitle,
  StatValue,
  StickyFooter,
  ViewButton,
  ViewToggle,
} from "@pages/admin/deals/products/style";

const StoreDealProducts = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { bucketUrl } = useConfig();
  const { data: user } = useSelector((state: any) => state.user);

  const [viewMode, setViewMode] = useState<"list" | "grid">("grid");
  const [deal, setDeal] = useState<any>(null);
  const [storeConfig, setStoreConfig] = useState<any>({
    title: "",
    priority: 0,
    image: null,
  });

  const [products, setProducts] = useState<any[]>([]);
  const [selectedProductsMap, setSelectedProductsMap] = useState<
    Record<string, any>
  >({});

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState<string>("");
  const [categories, setCategories] = useState<any[]>([]);

  // Load Deal and Pre-populate Data
  useEffect(() => {
    if (id) {
      DealsApi.find({ id }).then((res: any) => {
        setDeal(res);
        if (res.deals && Array.isArray(res.deals)) {
          // Find the entry for the current store
          const currentStoreDeal = res.deals.find(
            (item: any) =>
              item.storeId === user?.id || item.storeId === user?.storeId,
          );

          if (currentStoreDeal) {
            setStoreConfig({
              title: currentStoreDeal.title,
              priority: currentStoreDeal.priority || 0,
              image: currentStoreDeal.image,
            });

            const loadedProductMap: any = {};
            if (
              currentStoreDeal.products &&
              Array.isArray(currentStoreDeal.products)
            ) {
              currentStoreDeal.products.forEach((p: any) => {
                loadedProductMap[p.productId] = {
                  id: p.productId,
                  isSelected: true,
                  discountType: p.discountType,
                  discountValue: p.discountValue,
                  variations: p.variations,
                };
              });
            }
            setSelectedProductsMap(loadedProductMap);
          }
        }
      });
    }
  }, [id, user]);

  // Load Categories
  useEffect(() => {
    if (deal?.moduleId) {
      Category.list({
        params: { moduleId: deal.moduleId, parentId: 0 },
      })
        .then((cats: any) => {
          const list = Array.isArray(cats)
            ? cats
            : cats?.data || cats?.items || [];
          setCategories(Array.isArray(list) ? list : []);
        })
        .catch(console.error);
    }
  }, [deal?.moduleId]);

  // Fetch Products
  const fetchProducts = async () => {
    if (!deal || !user) return;
    setLoading(true);
    try {
      const res: any = await Product.list({
        params: {
          ...(filterCategory && { categoryId: filterCategory }),
          moduleId: deal.moduleId,
          storeId: user.storeId || user.id,
          limit: 100,
          search: searchTerm,
        },
      });
      const list = Array.isArray(res) ? res : res?.data || res?.items || [];
      const mappedList = list.map((p: any) => {
        const savedState = selectedProductsMap[p.id];
        if (savedState) {
          const merged = {
            ...p,
            isSelected: true,
            dealPrice: p.price,
            discountType: savedState.discountType || "percentage",
            discountValue: savedState.discountValue || 0,
            variations: p.variations?.map((v: any) => {
              const savedVar = savedState.variations?.find(
                (sv: any) => (sv.variationId || sv.id) === v.id,
              );
              return {
                ...v,
                dealPrice: v.price,
                discountType: savedVar?.discountType || "percentage",
                discountValue: savedVar?.discountValue || 0,
              };
            }),
          };
          // Hydrate selectedProductsMap with full details
          selectedProductsMap[p.id] = merged;
          return merged;
        }

        return {
          ...p,
          isSelected: false,
          dealPrice: p.price,
          discountType: "percentage",
          discountValue: 0,
          variations: p.variations?.map((v: any) => ({
            ...v,
            dealPrice: v.price,
            discountType: "percentage",
            discountValue: 0,
          })),
        };
      });
      setProducts(mappedList);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (deal) fetchProducts();
  }, [deal, filterCategory, searchTerm]);

  const toggleProductSelection = (productId: string) => {
    setProducts((prev) =>
      prev.map((p) => {
        if (p.id === productId) {
          const updated = { ...p, isSelected: !p.isSelected };
          const newMap = { ...selectedProductsMap };
          if (updated.isSelected) {
            newMap[updated.id] = updated;
          } else {
            delete newMap[updated.id];
          }
          setSelectedProductsMap(newMap);
          return updated;
        }
        return p;
      }),
    );
  };

  const updateProductDiscount = (
    productId: string,
    field: string,
    value: any,
  ) => {
    setProducts((prev) =>
      prev.map((p) => {
        if (p.id === productId) {
          let processedValue = value;
          if (field === "discountValue") {
            const numVal = parseFloat(value) || 0;
            const basePrice = p.priceBeforeDeal || p.price;
            if (p.discountType === "percentage") {
              processedValue = Math.min(100, Math.max(0, numVal));
            } else {
              processedValue = Math.min(basePrice, Math.max(0, numVal));
            }
          }
          const updated = { ...p, [field]: processedValue };
          if (updated.isSelected) {
            setSelectedProductsMap((prevMap) => ({
              ...prevMap,
              [updated.id]: updated,
            }));
          }
          return updated;
        }
        return p;
      }),
    );
  };

  const updateVariationDiscount = (
    productId: string,
    variationId: string,
    field: string,
    value: any,
  ) => {
    setProducts((prev) =>
      prev.map((p) => {
        if (p.id === productId && p.variations) {
          const updated = {
            ...p,
            variations: p.variations.map((v: any) => {
              if ((v.id || v._id) === variationId) {
                let processedValue = value;
                if (field === "discountValue") {
                  const numVal = parseFloat(value) || 0;
                  const basePrice = v.priceBeforeDeal || v.price;
                  if (v.discountType === "percentage") {
                    processedValue = Math.min(100, Math.max(0, numVal));
                  } else {
                    processedValue = Math.min(basePrice, Math.max(0, numVal));
                  }
                }
                return { ...v, [field]: processedValue };
              }
              return v;
            }),
          };
          if (updated.isSelected) {
            setSelectedProductsMap((prevMap) => ({
              ...prevMap,
              [updated.id]: updated,
            }));
          }
          return updated;
        }
        return p;
      }),
    );
  };

  const handleSave = async () => {
    const selectedProducts = products.filter((p) => p.isSelected);

    // Also include previously selected products that might not be in the current filtered/searched list
    const allSelected = Object.values(selectedProductsMap).filter(
      (p) => p.isSelected,
    );

    if (allSelected.length === 0) {
      toast.error("Please select at least one product");
      return;
    }

    const payload = {
      dealId: id,
      items: [
        {
          storeId: user.storeId || user.id,
          title: storeConfig.title,
          priority: storeConfig.priority,
          image: storeConfig.image,
          products: allSelected.map((p: any) => ({
            productId: p.id,
            discountType: p.discountType,
            discountValue: Number(p.discountValue),
            variations: p.variations?.map((v: any) => ({
              variationId: v.id || v._id,
              discountType: v.discountType,
              discountValue: Number(v.discountValue),
            })),
          })),
        },
      ],
    };

    setSubmitting(true);
    try {
      await DealsApi.addProducts(payload);
      toast.success("Products saved for this deal");
      navigate("/store/deals");
    } catch (e: any) {
      toast.error(e.message || "Failed to save products");
    } finally {
      setSubmitting(false);
    }
  };

  const totalSelectedCount = Object.keys(selectedProductsMap).length;

  return (
    <Wrapper>
      <Header title={`Manage Products: ${deal?.title || "Loading..."}`} />

      <StatsBar>
        <StatCard>
          <StatTitle>Total Selected</StatTitle>
          <StatValue color="#10b981">{totalSelectedCount}</StatValue>
        </StatCard>
        <StatCard>
          <StatTitle>Available Products</StatTitle>
          <StatValue>{products.length}</StatValue>
        </StatCard>
      </StatsBar>

      <ControlBar>
        <ControlContent>
          <LeftControls>
            <SearchWrapper>
              <SearchIconWrapper>
                <FiSearch size={16} color="#9ca3af" />
              </SearchIconWrapper>
              <SearchInput
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchWrapper>
          </LeftControls>

          <RightControls>
            <ViewToggle>
              <ViewButton
                active={viewMode === "list"}
                onClick={() => setViewMode("list")}
              >
                <FiList size={16} />
                List
              </ViewButton>
              <ViewButton
                active={viewMode === "grid"}
                onClick={() => setViewMode("grid")}
              >
                <FiGrid size={16} />
                Grid
              </ViewButton>
            </ViewToggle>

            <CategorySelect
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </CategorySelect>
          </RightControls>
        </ControlContent>
      </ControlBar>

      {loading ? (
        <LoadingContainer>
          <Spinner />
          <div>Loading products...</div>
        </LoadingContainer>
      ) : products.length === 0 ? (
        <EmptyState>
          <EmptyIcon>
            <FiPackage size={28} />
          </EmptyIcon>
          <div>No products found</div>
        </EmptyState>
      ) : (
        <ProductsGrid viewMode={viewMode}>
          {products.map((p) => (
            <ProductCard
              key={p.id}
              item={p}
              toggleSelection={() => toggleProductSelection(p.id)}
              updateProductDiscount={(id, field, value) =>
                updateProductDiscount(id, field, value)
              }
              updateVariationDiscount={(pId, vId, field, value) =>
                updateVariationDiscount(pId, vId, field, value)
              }
              bucketUrl={bucketUrl}
              currentDealId={id}
              settings={{}}
            />
          ))}
        </ProductsGrid>
      )}

      <StickyFooter>
        <FooterActions>
          <CountBadge>
            <CountLabel>Selection Count</CountLabel>
            <CountValue>{totalSelectedCount}</CountValue>
          </CountBadge>
          <div style={{ display: "flex", gap: "12px" }}>
            <Button
              title="Cancel"
              onClick={() => navigate("/store/deals")}
              type="button"
              styleButton="background: #f3f4f6; color: #374151; border: 1px solid #d1d5db;"
            />
            <Button
              title={submitting ? "Saving..." : "Save Products"}
              onClick={handleSave}
              disabled={submitting}
              type="button"
            />
          </div>
        </FooterActions>
      </StickyFooter>
    </Wrapper>
  );
};

export default StoreDealProducts;
